/* eslint-env browser */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

export function supportsWebLocks(): boolean {
  return typeof navigator !== 'undefined' && navigator.locks != null;
}

export type UseExclusiveWebLockOptions = {
  /**
   * When the Web Locks API is missing, treat this tab as holding the lock.
   * @default true
   */
  grantWhenApiMissing?: boolean;
  /** e.g. log a dev warning when falling back to multi-tab behavior */
  onMissingApi?: () => void;
};

export type UseExclusiveWebLockResult = {
  lockHeld: boolean;
  /** Another context holds the exclusive lock (only meaningful when `active`). */
  lockBlocked: boolean;
  /** `active` is true, acquisition was attempted, and another tab holds the lock. */
  isContentionBlocked: boolean;
  /**
   * While `active` is false, true if an `ifAvailable` probe could not take the lock (held elsewhere).
   * Cleared while `active` is true; use {@link isContentionBlocked} for contention during a session.
   */
  isLockHeldElsewhereWhileIdle: boolean;
  /** Re-run `ifAvailable` acquisition while `active` stays true (e.g. user clicked Retry). */
  retryAcquire: () => void;
  /**
   * Bump the acquire attempt if this tab is not already holding the lock.
   * Use when `active` may already be true so the effect does not re-run (e.g. second Sign in while blocked).
   */
  requestAcquireBump: () => void;
};

/**
 * Holds an exclusive {@link https://developer.mozilla.org/en-US/docs/Web/API/Web_Locks_API | Web Lock}
 * for `lockName` across tabs/windows on this origin while `active` is true.
 * Uses `{ ifAvailable: true }` so this tab does not wait indefinitely behind another holder.
 */
export function useExclusiveWebLock(
  lockName: string,
  active: boolean,
  options?: UseExclusiveWebLockOptions,
): UseExclusiveWebLockResult {
  const {
    grantWhenApiMissing = true,
    onMissingApi,
  } = options ?? {};

  const [lockHeld, setLockHeld] = useState(false);
  const [lockBlocked, setLockBlocked] = useState(false);
  const [heldElsewhereWhileIdle, setHeldElsewhereWhileIdle] = useState(false);
  const [acquireGeneration, setAcquireGeneration] = useState(0);
  const lockHeldRef = useRef(false);
  const onMissingApiRef = useRef(onMissingApi);
  onMissingApiRef.current = onMissingApi;

  useEffect(() => {
    lockHeldRef.current = lockHeld;
  }, [lockHeld]);

  /** Detect another tab holding the lock without acquiring it here (only while `active` is false). */
  useEffect(() => {
    if (active || !supportsWebLocks()) {
      setHeldElsewhereWhileIdle(false);
      return () => {};
    }

    let cancelled = false;

    const probe = () => {
      if (cancelled) return;
      navigator.locks
        .request(
          lockName,
          { ifAvailable: true },
          (lock) => {
            if (cancelled) {
              return undefined;
            }
            if (lock == null) {
              setHeldElsewhereWhileIdle(true);
              return undefined;
            }
            setHeldElsewhereWhileIdle(false);
            return undefined;
          },
        )
        .catch(() => undefined);
    };

    probe();
    window.addEventListener('focus', probe);
    const intervalId = window.setInterval(probe, 5000);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', probe);
      window.clearInterval(intervalId);
    };
  }, [active, lockName]);

  useEffect(() => {
    if (!active) {
      setLockHeld(false);
      setLockBlocked(false);
      return () => {};
    }

    if (!supportsWebLocks()) {
      if (grantWhenApiMissing) {
        onMissingApiRef.current?.();
        setLockHeld(true);
        setLockBlocked(false);
        return () => {
          setLockHeld(false);
        };
      }
      setLockHeld(false);
      setLockBlocked(true);
      return () => {};
    }

    let cancelled = false;
    let releaseLock: (() => void) | null = null;

    navigator.locks
      .request(
        lockName,
        { ifAvailable: true },
        // Lock callback returns a Promise to retain the lock until release (see Web Locks API).
        // eslint-disable-next-line consistent-return -- callback contract: void or Promise<void>
        (lock) => {
          if (cancelled) {
            return undefined;
          }
          if (lock == null) {
            setLockBlocked(true);
            setLockHeld(false);
            return undefined;
          }
          setLockBlocked(false);
          setLockHeld(true);
          return new Promise<void>((resolve) => {
            releaseLock = resolve;
          });
        },
      )
      .then(() => {
        if (!cancelled) {
          setLockHeld(false);
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      releaseLock?.();
    };
  }, [active, acquireGeneration, grantWhenApiMissing, lockName]);

  const retryAcquire = useCallback(() => {
    if (!active) return;
    setAcquireGeneration((g) => g + 1);
  }, [active]);

  const requestAcquireBump = useCallback(() => {
    if (lockHeldRef.current) return;
    setAcquireGeneration((g) => g + 1);
  }, []);

  const isContentionBlocked = active && lockBlocked && !lockHeld;
  const isLockHeldElsewhereWhileIdle = !active && heldElsewhereWhileIdle;

  return {
    lockHeld,
    lockBlocked,
    isContentionBlocked,
    isLockHeldElsewhereWhileIdle,
    retryAcquire,
    requestAcquireBump,
  };
}
